import { Button } from "@/components/ui/button";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { useMemo, useState } from "react";

const SKELETON_ROWS = ["alpha", "bravo", "charlie"];

type ContactEntry = {
	id?: string | number | null;
	name?: string | null;
	surname?: string | null;
	company?: string | null;
	phone?: string | null;
	cellphone?: string | null;
	address?: string | null;
	url?: string | null;
	email?: string | null;
	note?: string | null;
};

type ContactsTableProps = {
	contacts?: ContactEntry[];
	loading?: boolean;
	getAvatar: (email?: string | null) => string;
	onEdit: (contact: ContactEntry) => void;
	onDelete: (contact: ContactEntry) => void;
};

const buildFullName = (contact: ContactEntry) =>
	`${contact.name ?? ""} ${contact.surname ?? ""}`.trim() || "-";

export function ContactsTable({
	contacts = [],
	loading = false,
	getAvatar,
	onEdit,
	onDelete,
}: ContactsTableProps) {
	const [expandedIds, setExpandedIds] = useState<Array<string>>([]);

	const rows = useMemo(
		() =>
			contacts.map((contact) => {
				const key =
					contact.id ?? contact.email ?? contact.name ?? "contact-row";
				return {
					key: String(key),
					contact,
				};
			}),
		[contacts],
	);

	const toggleRow = (key: string) => {
		setExpandedIds((current) =>
			current.includes(key)
				? current.filter((item) => item !== key)
				: [...current, key],
		);
	};

	const renderSkeleton = () => (
		<TableBody>
			{SKELETON_ROWS.map((rowKey) => (
				<TableRow key={`contacts-${rowKey}`} className="animate-pulse">
					<TableCell>
						<div className="h-4 w-10 rounded bg-muted/70" />
					</TableCell>
					<TableCell>
						<div className="h-8 w-8 rounded-full bg-muted/60" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-32 rounded bg-muted/50" />
					</TableCell>
					<TableCell>
						<div className="h-4 w-24 rounded bg-muted/40" />
					</TableCell>
					<TableCell>
						<div className="h-8 w-24 rounded bg-muted/30" />
					</TableCell>
				</TableRow>
			))}
		</TableBody>
	);

	return (
		<div className="overflow-hidden rounded-lg border border-border/60 bg-card/70">
			<Table>
				<TableHeader>
					<TableRow className="bg-muted/50">
						<TableHead className="w-[70px]" />
						<TableHead className="w-[70px]" />
						<TableHead>Nome</TableHead>
						<TableHead>Telemóvel</TableHead>
						<TableHead className="w-[150px]" />
					</TableRow>
				</TableHeader>
				{loading ? (
					renderSkeleton()
				) : (
					<TableBody>
						{rows.map(({ key, contact }) => {
							const isExpanded = expandedIds.includes(key);
							return (
								<TableRow key={key}>
									<TableCell>
										<Button
											size="sm"
											variant="secondary"
											onClick={() => toggleRow(key)}
										>
											{isExpanded ? "Menos" : "Mais"}
										</Button>
									</TableCell>
									<TableCell>
										<img
											src={getAvatar(contact.email)}
											className="tp-ContactList__avatar img-circle"
											alt="Avatar"
										/>
									</TableCell>
									<TableCell>{buildFullName(contact)}</TableCell>
									<TableCell>{contact.cellphone ?? "-"}</TableCell>
									<TableCell>
										<div className="flex justify-end gap-2">
											<Button
												size="sm"
												variant="secondary"
												onClick={() => onEdit(contact)}
											>
												Editar
											</Button>
											<Button
												size="sm"
												variant="destructive"
												onClick={() => onDelete(contact)}
											>
												Apagar
											</Button>
										</div>
									</TableCell>
								</TableRow>
							);
						})}
						{rows.map(({ key, contact }) =>
							expandedIds.includes(key) ? (
								<TableRow key={`${key}-details`}>
									<TableCell colSpan={5} className="bg-muted/30">
										<div className="space-y-1 text-sm">
											<p>Empresa: {contact.company ?? "N/A"}</p>
											<p>Telefone: {contact.phone ?? "N/A"}</p>
											{contact.address ? <p>{contact.address}</p> : null}
											{contact.url ? (
												<p>
													<a
														href={contact.url}
														target="_blank"
														rel="noreferrer"
													>
														Website
													</a>
												</p>
											) : null}
											{contact.email ? (
												<p>
													Email:{" "}
													<a href={`mailto:${contact.email}`}>
														{contact.email}
													</a>
												</p>
											) : null}
											<p>Notas: {contact.note ?? "N/A"}</p>
										</div>
									</TableCell>
								</TableRow>
							) : null,
						)}
					</TableBody>
				)}
			</Table>
		</div>
	);
}
